/**
 * Consumer Notification Hooks
 *
 * Type-safe hooks for consumer notification management using tRPC.
 * Provides notification list, unread count, mark-as-read, and preferences.
 *
 * @see Issue #699 - Notification System Fidelity
 * @module hooks/useNotifications
 */

import { trpc } from '@/lib/trpc/client';
import {
  useConsumerNotificationPreferences,
  useConsumerNotificationsList,
  useConsumerNotificationsUnreadCount,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useMarkNotificationAsUnread,
  useUpdateConsumerNotificationPreferences,
} from '@/lib/trpc/hooks';

// Re-export with consumer-friendly names
export function useNotifications(input?: { page?: number; limit?: number; status?: 'all' | 'unread' | 'read' }) {
  return useConsumerNotificationsList(input);
}

export function useUnreadCount() {
  return useConsumerNotificationsUnreadCount();
}

export function useMarkAsRead() {
  return useMarkNotificationAsRead();
}

export function useMarkAsUnread() {
  return useMarkNotificationAsUnread();
}

export function useMarkAllRead() {
  return useMarkAllNotificationsAsRead();
}

export function useNotificationPreferences() {
  return useConsumerNotificationPreferences();
}

export function useUpdateNotificationPreferences() {
  return useUpdateConsumerNotificationPreferences();
}

// ============================================================================
// PUSH NOTIFICATION TOKEN HOOKS
// ============================================================================

/**
 * Register a push notification token with the backend
 * @see Issue #699 - Push Notification FCM Token Management
 */
export function useRegisterPushToken() {
  return trpc.pushNotifications.registerToken.useMutation();
}

/**
 * Revoke a push notification token from the backend
 * @see Issue #699 - Push Notification FCM Token Management
 */
export function useRevokePushToken() {
  return trpc.pushNotifications.revokeToken.useMutation();
}

export default useNotifications;
